import React from 'react';
import { connect } from 'react-redux'
import { withRouter } from 'next/router'
import * as qs from 'qs';

/**
 * Injects functions into supplied WrappedComponent that provide functionality to update URL to include parameters according to their current values.
 * 
 * @param {*} WrappedComponent the component to wrap, into which the functionality to updateURL will be injected. Said functions must be injected into
 * WrappedComponent so that it triggers the update as needed.
 * @param {*} stateToParamsMap a field - paramName map that allows to map from the field key (as it will be notified to the update function), to a name
 * that will be used in the URL as the parameter name. E.g:
 * stateToParamsMap: {"myField": "paramField1"}
 * keyValueMap (as supplied to updateURL function): {"myField": "value123"}
 * resulting URL: https://example.com/somePage?paramField1=value123
 */
function withRouterParams(WrappedComponent, stateToParamsMap = {}) {

    class RouterWithParams extends React.Component {

        constructor(props) {
            super(props);
            const _this = this;
            WrappedComponent.prototype.updateURL = function (keyValueMap, clearPrevious) {
                return _this.updateURL(keyValueMap, clearPrevious);
            };
            WrappedComponent.prototype.updateURLFromFormik = function (formikState, clearPrevious) {
                return _this.updateURLFromFormik(formikState, clearPrevious);
            };
            WrappedComponent.prototype.getParams = function () {
                return _this.getParams();
            };
        }

        getParams() {
            let params = {};
            if (typeof window !== "undefined") {
                const {location} = window;
                if (location && location.search) {
                    params = qs.parse(location.search.slice(1));
                }
                return params;
            }
        }

        /**
         * Updates the current URL to include the parameters specified in constructor by stateToParamsMap, according to the updated values supplied to this
         * function. All previous query parameters will be retained, unless a null or undefined value is provided for said parameter.
         * 
         * @param {*} keyValueMap key - value map of updated state. Every key in this map that has been marked to be observed (by stateToParamsMap provided
         * to constructor), will be used to update the query.
         * @param {boolean} clearPrevious whether to clear any other params in current URL that haven't been passed as arguments in this call. Default: false.
         */
        updateURL(keyValueMap = {}, clearPrevious = false) {
            // all params currently in URL, which we will keep unless their associated state was updated with null/undefined, or if clearPrevious param is set to true
            const queryParams = clearPrevious ? {} : this.props.router.query;

            Object.keys(keyValueMap).forEach(function (key) {
                // if it's one of the keys we are observing
                if (key in stateToParamsMap) {
                    const paramName = stateToParamsMap[key];
                    // if an observed property was updated (to something other than null/undefined), add/replace param with new value or delete if empty
                    keyValueMap[key] ? queryParams[paramName] = keyValueMap[key] : delete queryParams[paramName];
                }
            });

            let queryString = [];
            Object.keys(queryParams).forEach(function (key) {
                queryString.push(key + "=" + queryParams[key]);
            });

            queryString = queryString.join("&");
            let newPath = this.props.router.pathname + (queryString ? "?" + queryString : "");

            this.props.router.push(newPath, newPath, { shallow: true })
        }

        /**
         * Utility method to handle updates from Formik state.
         * formik-effect is needed to trigger events upon updates within Formik.
         * 
         * @param {object} formikState The Formik's state object. Any field that is marked as touched will be passed on as argument for the update,
         * along with the provided value, if marked as valid, or null otherwise. Whether and how the URL is updated depends on updateURL function.
         * @param {boolean} clearPrevious whether to clear any other params in current URL that haven't been passed as arguments in this call. Default: false.
         */
        updateURLFromFormik(formikState = {}, clearPrevious = false) {
            if(formikState) {
                const keyValueMap = {};
                Object.keys(formikState.touched).forEach(function (key) {
                    if (formikState.touched[key] && !(key in formikState.errors)) {
                        keyValueMap[key] = formikState.values[key];
                    } else {
                        // if value is invalid, "clear" the URL param
                        keyValueMap[key] = null;
                    }
                });
                this.updateURL(keyValueMap, clearPrevious);
            }
        }

        render() {
            return (
                <WrappedComponent
                    {...this.props}
                />
            );
        }
    }

    const mapStateToProps = () => {
        return {};
    };

    return connect(mapStateToProps, null)(withRouter(RouterWithParams));
}

export default withRouterParams;